import type { RuleSet } from '../../rules';
import type { Building, Cell, EntityId, PlayerId, Unit } from '../../sim';
import type { SendTarget } from '../sendTroops';
import type { EntitySummary } from './summary';
import { hexDistance, type LossTally, type TrainedTally } from './types';

/** How notifications name things and places. */

/**
 * "Warriors need Barracks 1": the units that need a `buildingType` at its
 * lowest required level (what a city without one can no longer train).
 */
export function lockedText(rules: RuleSet, buildingType: string): string | null {
  const needing = Object.values(rules.units).filter((u) => u.requires?.building === buildingType);
  if (needing.length === 0) return null;
  const level = Math.min(...needing.map((u) => u.requires?.level ?? 1));
  const names = needing
    .filter((u) => (u.requires?.level ?? 1) === level)
    .map((u) => (u.name.endsWith('y') || u.name.endsWith('s') ? u.name : `${u.name}s`));
  const list =
    names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `${list} need ${rules.buildings[buildingType]?.name ?? buildingType} ${level}`;
}

/**
 * A loss tally's text. Units: "Warrior lost near City 1", "5 units lost near
 * City 1: 3 Warriors, 2 Archers", "Group 2 wiped out near City 1: 4
 * Warriors". Buildings: "Farm lost near City 2", "Barracks site destroyed
 * near City 2", "3 buildings lost near City 1: 2 Farms, 1 Mine".
 */
export function tallyText(tally: LossTally): string {
  const entries = Object.entries(tally.counts).sort(
    ([a, m], [b, n]) => n - m || a.localeCompare(b)
  );
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const list = entries.map(([name, n]) => `${n} ${n === 1 ? name : pluralName(name)}`).join(', ');
  const place = tally.place ? ` ${tally.place}` : '';
  const groups = tally.groups ?? [];
  if (groups.length > 0) {
    const numbers = [...groups].sort((a, b) => a - b);
    const nameOf = (n: number) => tally.groupNames?.[n] ?? `Group ${n}`;
    const which =
      numbers.length === 1
        ? nameOf(numbers[0])
        : `${numbers
            .slice(0, -1)
            .map(nameOf)
            .join(', ')} and ${nameOf(numbers[numbers.length - 1])}`;
    return `${which} wiped out${place}: ${list}`;
  }
  if (total === 1) {
    const name = entries[0]?.[0] ?? '';
    return name.endsWith(' site') ? `${name} destroyed${place}` : `${name} lost${place}`;
  }
  return `${total} ${tally.what} lost${place}: ${list}`;
}

/**
 * A trained tally's text, without the number (the count chip carries it):
 * "Warrior trained in City 1", "Warriors trained in City 1", "Warriors,
 * Archers trained in City 1".
 */
export function trainedText(tally: TrainedTally): string {
  const entries = Object.entries(tally.counts).sort(
    ([a, m], [b, n]) => n - m || a.localeCompare(b)
  );
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const names = entries.map(([name]) => (total === 1 ? name : pluralName(name))).join(', ');
  return `${names} trained in ${tally.city}`;
}

/** Compass direction from `from` to `to` on the map (north is up, -r). */
export function compass(from: Cell, to: Cell): string {
  // Pointy-top axial → screen-like x (east) and y (south).
  const x = to.q - from.q + (to.r - from.r) / 2;
  const y = ((to.r - from.r) * Math.sqrt(3)) / 2;
  const dirs = [
    'east',
    'south-east',
    'south',
    'south-west',
    'west',
    'north-west',
    'north',
    'north-east',
  ];
  const i = Math.round(Math.atan2(y, x) / (Math.PI / 4));
  return dirs[(i + 8) % 8];
}

/**
 * Where a unit fell, from the nearest own city: "near City 2" within 3
 * tiles, else "9 tiles east of City 2".
 */
export function whereFrom(
  pos: Cell,
  cities: ReadonlyArray<[EntityId, EntitySummary]>,
  cityLabel?: (cityId: EntityId) => string
): string {
  let best: [EntityId, EntitySummary] | undefined;
  let bestD = Number.POSITIVE_INFINITY;
  for (const c of cities) {
    const d = hexDistance(pos, c[1].pos);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  if (!best) return '';
  const name = cityLabel?.(best[0]) ?? 'your city';
  if (bestD <= 3) return `near ${name}`;
  return `${bestD} tiles ${compass(best[1].pos, pos)} of ${name}`;
}

/** A rival unit or building as something to send troops at: "Jolly Swagman's Warrior". */
export function targetOf(
  rules: RuleSet,
  e: Unit | Building,
  playerName: (id: PlayerId) => string = (id) => id
): SendTarget {
  return {
    id: e.id,
    pos: { ...e.pos },
    label: `${playerName(e.owner)}'s ${entityName(rules, e)}`,
  };
}

export function entityName(rules: RuleSet, e: Unit | Building): string {
  return 'buildProgress' in e
    ? (rules.buildings[e.type]?.name ?? e.type)
    : (rules.units[e.type]?.name ?? e.type);
}

/** "Tanks", "Cities", "Farm sites"; mass nouns (Artillery, Cavalry) and ones in -s (Barracks) stay as they are. */
export function pluralName(name: string): string {
  if (/(artillery|cavalry|s)$/i.test(name)) return name;
  return name.endsWith('y') ? `${name.slice(0, -1)}ies` : `${name}s`;
}
